import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Globe, FileText, Download, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssessmentCard = ({ assessment }) => (
  <div className="card" style={{ marginBottom: '1rem', background: '#ffffff' }} data-testid={`assessment-${assessment.assessment_id}`}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
      <div>
        <h3 style={{ fontWeight: 700, color: '#302B2C' }}>Assessment</h3>
        <p style={{ fontSize: '0.875rem', color: '#8B827D' }}>
          {new Date(assessment.created_at).toLocaleDateString()}
        </p>
      </div>
      <span className="badge badge-success">
        Completed
      </span>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {assessment.eligibility_results.slice(0, 3).map((result) => (
        <div key={`${result.country}-${result.program}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#FFF6EB', borderRadius: '2px' }}>
          <div>
            <p style={{ fontWeight: 600, color: '#302B2C' }}>{result.country}</p>
            <p style={{ fontSize: '0.875rem', color: '#57504E' }}>{result.program}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#F67425' }}>{result.total_points}</p>
            <p style={{ fontSize: '0.75rem', color: '#8B827D' }}>points</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ReportCard = ({ report, onDownload }) => (
  <div className="card" style={{ marginBottom: '1rem', background: '#ffffff' }} data-testid={`report-${report.report_id}`}>
    <div style={{ display: 'flex', gap: '1rem' }}>
      <div style={{ width: '48px', height: '48px', background: 'rgba(246, 116, 37, 0.1)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <FileText size={24} color="#F67425" />
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ fontWeight: 700, color: '#302B2C', marginBottom: '0.25rem' }}>Eligibility Report</h3>
        <p style={{ fontSize: '0.875rem', color: '#8B827D', marginBottom: '1rem' }}>
          Generated {new Date(report.generated_at).toLocaleDateString()}
        </p>
        <button
          onClick={() => onDownload(report.report_id)}
          className="btn btn-primary"
          style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
          data-testid={`download-report-btn-${report.report_id}`}
        >
          <Download size={16} />
          <span>Download PDF</span>
        </button>
      </div>
    </div>
  </div>
);

const EmptyCard = ({ message }) => (
  <div className="card" style={{ textAlign: 'center', padding: '3rem', background: '#ffffff' }}>
    <FileText size={48} color="#B9AFA8" style={{ margin: '0 auto 1rem' }} />
    <p style={{ color: '#57504E' }}>{message}</p>
  </div>
);

const Dashboard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/dashboard/${userId}`);
      setDashboardData(response.data);
    } catch (err) {
      console.error('Failed to load dashboard', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const downloadReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/report/${reportId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `axpats_report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Report download failed', err);
      alert('Download failed — please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FFF6EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={48} color="#F67425" className="animate-spin" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ minHeight: '100vh', background: '#FFF6EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#57504E' }}>No data found</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FFF6EB' }}>
      <header className="app-header">
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Globe size={32} color="#F67425" />
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#302B2C' }}>axpats</span>
          </div>
        </div>
      </header>

      <div className="container" style={{ padding: '3rem 1.5rem' }} data-testid="dashboard-container">
        <div className="card" style={{ marginBottom: '2rem', background: '#ffffff' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#302B2C', marginBottom: '0.5rem' }}>
            Welcome, {dashboardData.user.full_name}
          </h1>
          <p style={{ color: '#57504E' }}>{dashboardData.user.email}</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: '1.5rem' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#302B2C', marginBottom: '1rem' }}>Your Assessments</h2>
            {dashboardData.assessments.length === 0 ? (
              <EmptyCard message="No assessments yet" />
            ) : (
              dashboardData.assessments.map((assessment) => (
                <AssessmentCard key={assessment.assessment_id || assessment.created_at} assessment={assessment} />
              ))
            )}
          </div>

          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#302B2C', marginBottom: '1rem' }}>Your Reports</h2>
            {dashboardData.reports.length === 0 ? (
              <EmptyCard message="No reports yet" />
            ) : (
              dashboardData.reports.map((report) => (
                <ReportCard key={report.report_id} report={report} onDownload={downloadReport} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
